/**
 * Swiper Product Gallery JavaScript
 * Handles initialization and management of Swiper sliders for product media
 */

class SwiperProductGallery {
  constructor(sectionId, settings) {
    this.sectionId = sectionId;
    this.settings = settings;
    this.swiperMain = null;
    this.swiperThumbs = null;
    
    this.init();
  }
  
  init() {
    // Wait for Swiper library to load
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }
    
    this.initializeThumbnails();
    this.initializeMainSwiper();
    this.bindEvents();
  }
  
  initializeThumbnails() {
    // Thumbnails disabled for this design
    return;
  }
  
  initializeMainSwiper() {
    const mainElement = document.querySelector(`#SwiperMain-${this.sectionId}`);
    if (!mainElement) return;
    
    const config = {
      speed: this.settings.swiper_speed,
      effect: this.settings.swiper_effect,
      spaceBetween: 10,
      
      // Keyboard navigation
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      
      // Touch gestures
      touchRatio: 1,
      touchAngle: 45,
      
      on: {
        init: () => {
          console.log('Swiper main gallery initialized');
          this.updateMediaFocus();
        },
        slideChange: () => {
          this.updateMediaFocus();
          this.handleMediaAutoplay();
        }
      }
    };
    
    // Loop configuration
    if (this.settings.swiper_loop) {
      config.loop = true;
    }
    
    // Autoplay configuration
    if (this.settings.swiper_autoplay) {
      config.autoplay = {
        delay: this.settings.swiper_autoplay_delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }
    
    // Effect-specific configurations
    if (this.settings.swiper_effect === 'cube') {
      config.cubeEffect = {
        shadow: true,
        slideShadows: true,
        shadowOffset: 20,
        shadowScale: 0.94,
      };
    } else if (this.settings.swiper_effect === 'coverflow') {
      config.coverflowEffect = {
        rotate: 50,
        stretch: 0,
        depth: 100,
        modifier: 1,
        slideShadows: true,
      };
    } else if (this.settings.swiper_effect === 'fade') {
      config.fadeEffect = {
        crossFade: true,
      };
    }
    
    // Navigation configuration
    if (this.settings.swiper_navigation) {
      config.navigation = {
        nextEl: `#SwiperMain-${this.sectionId} .swiper-button-next`,
        prevEl: `#SwiperMain-${this.sectionId} .swiper-button-prev`,
      };
    }
    
    // Pagination configuration
    if (this.settings.swiper_pagination) {
      config.pagination = {
        el: `#SwiperMain-${this.sectionId} .swiper-pagination`,
        clickable: true,
        dynamicBullets: false,
      };
    }
    
    // Thumbnails disabled for this design
    
    this.swiperMain = new Swiper(`#SwiperMain-${this.sectionId}`, config);
  }
  
  updateMediaFocus() {
    // Update focus for accessibility
    const activeSlide = document.querySelector(`#SwiperMain-${this.sectionId} .swiper-slide-active`);
    if (activeSlide) {
      const focusableElement = activeSlide.querySelector('img, video, button');
      if (focusableElement) {
        focusableElement.focus();
      }
    }
  }
  
  handleMediaAutoplay() {
    // Pause/play videos based on active slide
    const allVideos = document.querySelectorAll(`#SwiperMain-${this.sectionId} video`);
    allVideos.forEach(video => {
      video.pause();
    });
    
    const activeVideo = document.querySelector(`#SwiperMain-${this.sectionId} .swiper-slide-active video`);
    if (activeVideo) {
      activeVideo.play();
    }
  }
  
  bindEvents() {
    // Handle variant changes
    document.addEventListener('variant:change', (event) => {
      if (event.detail.sectionId === this.sectionId) {
        this.handleVariantChange(event.detail.variant);
      }
    });
    
    // Handle resize
    window.addEventListener('resize', this.debounce(() => {
      if (this.swiperMain) {
        this.swiperMain.update();
      }
      if (this.swiperThumbs) {
        this.swiperThumbs.update();
      }
    }, 250));
  }
  
  handleVariantChange(variant) {
    if (!variant || !variant.featured_media) return;
    
    const targetSlide = document.querySelector(`[data-media-id="${this.sectionId}-${variant.featured_media.id}"]`);
    if (targetSlide && this.swiperMain) {
      const slideIndex = Array.from(targetSlide.parentNode.children).indexOf(targetSlide);
      this.swiperMain.slideTo(slideIndex);
    }
  }
  
  // Utility function for debouncing
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  // Public methods for external control
  slideTo(index) {
    if (this.swiperMain) {
      this.swiperMain.slideTo(index);
    }
  }
  
  slideNext() {
    if (this.swiperMain) {
      this.swiperMain.slideNext();
    }
  }
  
  slidePrev() {
    if (this.swiperMain) {
      this.swiperMain.slidePrev();
    }
  }
  
  destroy() {
    if (this.swiperMain) {
      this.swiperMain.destroy(true, true);
    }
    if (this.swiperThumbs) {
      this.swiperThumbs.destroy(true, true);
    }
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Find all Swiper galleries and initialize them
  const swiperGalleries = document.querySelectorAll('[data-swiper-gallery]');
  
  swiperGalleries.forEach(gallery => {
    const sectionId = gallery.dataset.sectionId;
    const settings = JSON.parse(gallery.dataset.swiperSettings || '{}');
    
    new SwiperProductGallery(sectionId, settings);
  });
});

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.SwiperProductGallery = SwiperProductGallery;
}